/* A "?" beside a control, explaining when to reach for it.
 *
 * For the case where a feature is obvious once you have a use for it and
 * baffling until then — sub-albums being the first. The alternative is a
 * paragraph of help text every user reads once and then reads past forever.
 *
 * Click to open rather than hover: hover tooltips do not exist on a phone, and
 * Expert Studio is used on phones.
 */
import { useEffect, useRef, useState } from 'react';
import { Icon } from '@/lib/icons';

export function Hint({ children, label = 'What is this?' }: { children: React.ReactNode; label?: string }) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    const onDown = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  return (
    <span className="es-hintwrap" ref={wrap}>
      <button type="button" className="es-hintbtn" aria-label={label} aria-expanded={open}
        onClick={() => setOpen((v) => !v)}>
        <Icon name="info" size={13} />
      </button>
      {open && <span className="es-hintpop" role="note">{children}</span>}
    </span>
  );
}
